/**
 * 給与計算用のメンバーテーブル。行 = worker、列 = メンバー DB の給与計算関連項目。
 *
 * 列の一次ソースは payroll item カタログ（`payrollItemCatalog.ts`）の `category: 'reference'`。
 * 供給元が従業員マスタ(People) の R001–R010 / R014 / R016–R019 / R041–R044 と、
 * 申告データの R020 / R046 を全て列として持ち、各列は `payrollItemId` でカタログを名指しする。
 * 表示用の `code` は利用者が編集し得るので永続化せず、コメントでの言及にとどめる。
 *
 * 列順は 氏名 / 社員番号 → payroll item を code 昇順 → payroll item に無い細目 の順。
 * カタログとの突合を目で追えるようにするため、読みやすさより code 順を優先している。
 *
 * 末尾の細目 9 列は payroll item に無いが給与計算に要る項目。`sourcePath` の出所は
 * 一様ではなく、次の 3 群に分かれる（「全部が入力スナップショット由来」ではない）:
 *
 * (a) 給与計算入力スナップショット（`payrollInputSnapshots.ts` の `PEOPLE_SEEDS`）と一致 …
 *     生年月日 / 世帯主区分 / 給与振込口座。表記もそのまま使っている。
 * (b) スナップショットに項目自体が無く、People 側の項目ラベルから起こしたもの …
 *     健康保険証番号 / 厚生年金保険番号 / 雇用保険の被保険者番号。
 *     payroll item カタログにも入力スナップショットにも無いが、社会保険の届出に要るので置いた。
 *     根の `メンバー › 社会保険（日本）` は People の実在する権限カテゴリ
 *     （`mocks/fixtures/category-permission-nodes.ts`）。
 *     R014 障害者区分も同様で、スナップショットの「障害者区分」とはラベルが揃っていない。
 * (c) スナップショットに実在するが、**意図的に根を付け替えた**もの …
 *     健康保険の標準報酬月額 / 厚生年金保険の標準報酬月額 / 介護保険第2号被保険者区分。
 *     スナップショットはこの 3 つを `福利厚生 › 社会保険 › …` から凍結する扱いだが、
 *     この参照先は `coreSource: 'people'`（メンバー DB の抽出）なので
 *     `メンバー › 社会保険（日本） › …` に寄せてある。どちらかが誤りというより、
 *     凍結の単位が違うことによる衝突。スナップショット側の表記に戻すと
 *     `coreSource` と矛盾するため、ここでは戻さない。
 *
 * R020 扶養親族等の数 / R046 税表区分の `申告データ ›` も同様に付け替えた根で、
 * スナップショットのカテゴリ（people / attendances / organization / contracts / benefits /
 * commuting-expenses）には存在しない。payroll item カタログが両項目の供給元を
 * 「扶養控除等申告書・年末調整（申告データ）」と明示しているため、カタログの供給元区分に
 * 従って新設した。スナップショット側は同じ値を `メンバー › 扶養家族 › …` /
 * `メンバー › 税務情報 › …` から引く扱いになっている。
 *
 * 値はサンプル（架空の従業員）。照会先カタログの「労働者別 住民税額」と同じ 10 名・同じ並びで、
 * 住民税課税市区町村（R002）もその表と一致させている（参照先 → 照会先の突合が成立するようにするため）。
 */

import type { PayrollReferenceCatalogTable } from '../../types';

/**
 * 給与計算用のメンバーテーブル。
 *
 * 値の整合で守っていること:
 * - 年齢（R004）は生年月日から導いた給与計算基準日 2026-08-11 時点の値。介護保険第2号被保険者は
 *   その年齢が 40 歳以上 65 歳未満の行だけ「該当」。
 * - 等級（R005）× 号俸（R009）は照会先カタログの号俸表の組で、契約テーブルの基本給と一致する。
 *   号俸表を持たない管理職（R006 が部長・課長）と時給・日給の労働者は「—」。
 * - 役職（R006）は照会先カタログの役職手当 単価表の役職で、契約テーブルの役職手当と一致する。
 * - 報酬月額（R041）= 契約テーブルの 基本給（時給・日給は単価 × 勤怠実績）+ 役職手当 +
 *   固定残業手当 + 定期代（R008）。標準報酬月額はこれを協会けんぽの等級に丸めた額。
 * - 健康保険と厚生年金の標準報酬月額は、いずれも下限 88,000 円・上限 650,000 円の範囲に収まる
 *   行しかないため同額になっている（範囲外なら厚生年金側だけ頭打ちになる）。
 * - 扶養家族数（R007）は実数、扶養親族等の数（R020）は源泉徴収税額表上の数。本人が障害者の行
 *   （EMP-1004）だけ R020 が R007 より 1 多くなる。この 2 列は供給元が別（People / 申告データ）で
 *   値も一致しないため、片方に寄せてはいけない。
 * - 税表区分（R046）は全行が甲。乙（従たる給与）の労働者は住民税の特別徴収を主たる給与支払者側で
 *   行うため、照会先カタログの「労働者別 住民税額（特別徴収税額通知）」に同じ 10 名が
 *   載っていることと矛盾させないため。
 * - 通勤支払区分（R019）は全行が定期。実費区分は定期代を持たず報酬月額の内訳が変わるので、
 *   この表では定期に揃えて R041 を定期代から検算できる形にしている。
 */
export const payrollReferenceMemberTable: PayrollReferenceCatalogTable = {
  columns: [{
    label: '氏名',
    sourcePath: 'メンバー › 基本情報 › 氏名'
  }, {
    label: '社員番号',
    sourcePath: 'メンバー › 従業員ID › 従業員番号'
  },
  // R001
  {
    label: '住民票住所',
    payrollItemId: 'pi-r001',
    sourcePath: 'メンバー › 住所情報 › 住民票住所'
  },
  // R002
  {
    label: '住民税課税市区町村',
    payrollItemId: 'pi-r002',
    sourcePath: 'メンバー › 住所情報 › 住民票住所（市区町村）'
  },
  // R003
  {
    label: '住民票住所の都道府県',
    payrollItemId: 'pi-r003',
    sourcePath: 'メンバー › 住所情報 › 住民票住所（都道府県）'
  },
  // R004
  {
    align: 'end',
    label: '年齢（歳）',
    payrollItemId: 'pi-r004',
    sourcePath: 'メンバー › 基本情報 › 生年月日（導出）'
  },
  // R005
  {
    label: '等級',
    payrollItemId: 'pi-r005',
    sourcePath: 'メンバー › 給与報酬情報 › 等級'
  },
  // R006
  {
    label: '役職',
    payrollItemId: 'pi-r006',
    sourcePath: 'メンバー › 従業員配属情報 › 役職'
  },
  // R007
  {
    align: 'end',
    label: '扶養家族数（人）',
    payrollItemId: 'pi-r007',
    sourcePath: 'メンバー › 扶養家族 › 扶養家族数'
  },
  // R008
  {
    align: 'end',
    label: '定期代（円）',
    payrollItemId: 'pi-r008',
    sourcePath: 'メンバー › 通勤交通情報 › 定期代'
  },
  // R009
  {
    label: '号俸',
    payrollItemId: 'pi-r009',
    sourcePath: 'メンバー › 給与報酬情報 › 号俸'
  },
  // R010
  {
    label: '勤務地',
    payrollItemId: 'pi-r010',
    sourcePath: 'メンバー › 勤務情報 › 勤務地'
  },
  // R014
  {
    label: '障害者区分',
    payrollItemId: 'pi-r014',
    sourcePath: 'メンバー › 基本情報 › 障害区分'
  },
  // R016
  {
    label: '健康保険組合',
    payrollItemId: 'pi-r016',
    sourcePath: 'メンバー › 社会保険（日本） › 健康保険組合'
  },
  // R017
  {
    label: '雇用保険加入区分',
    payrollItemId: 'pi-r017',
    sourcePath: 'メンバー › 社会保険（日本） › 雇用保険加入区分'
  },
  // R018
  {
    label: '厚生年金加入区分',
    payrollItemId: 'pi-r018',
    sourcePath: 'メンバー › 社会保険（日本） › 厚生年金加入区分'
  },
  // R019
  {
    label: '通勤支払区分（定期・実費）',
    payrollItemId: 'pi-r019',
    sourcePath: 'メンバー › 通勤交通情報 › 通勤支払区分'
  },
  // R020
  {
    align: 'end',
    label: '扶養親族等の数（税額表適用）（人）',
    payrollItemId: 'pi-r020',
    sourcePath: '申告データ › 扶養控除等申告書 › 扶養親族等の数'
  },
  // R041
  {
    align: 'end',
    label: '報酬月額（円）',
    payrollItemId: 'pi-r041',
    sourcePath: 'メンバー › 社会保険（日本） › 報酬月額'
  },
  // R042
  {
    label: '社会保険適用区分',
    payrollItemId: 'pi-r042',
    sourcePath: 'メンバー › 社会保険（日本） › 社会保険適用区分'
  },
  // R043
  {
    label: '随時改定該当区分',
    payrollItemId: 'pi-r043',
    sourcePath: 'メンバー › 社会保険（日本） › 随時改定該当区分'
  },
  // R044
  {
    label: '産休育休保険料免除区分',
    payrollItemId: 'pi-r044',
    sourcePath: 'メンバー › 社会保険（日本） › 産休育休保険料免除区分'
  },
  // R046
  {
    label: '税表区分（甲乙丙）',
    payrollItemId: 'pi-r046',
    sourcePath: '申告データ › 扶養控除等申告書 › 税表区分'
  },
  // ここから下は payroll item カタログに無いが給与計算に要る細目（入力スナップショット由来）。
  {
    label: '生年月日',
    sourcePath: 'メンバー › 基本情報 › 生年月日'
  }, {
    label: '世帯主区分',
    sourcePath: 'メンバー › 基本情報 › 世帯主区分'
  }, {
    label: '給与振込口座',
    sourcePath: 'メンバー › 口座情報 › 給与振込口座'
  }, {
    label: '健康保険証番号',
    sourcePath: 'メンバー › 社会保険（日本） › 健康保険証番号'
  }, {
    label: '厚生年金保険番号',
    sourcePath: 'メンバー › 社会保険（日本） › 厚生年金保険番号'
  }, {
    label: '雇用保険の被保険者番号',
    sourcePath: 'メンバー › 社会保険（日本） › 雇用保険の被保険者番号'
  }, {
    align: 'end',
    label: '健康保険の標準報酬月額（円）',
    sourcePath: 'メンバー › 社会保険（日本） › 健康保険の標準報酬月額'
  }, {
    align: 'end',
    label: '厚生年金保険の標準報酬月額（円）',
    sourcePath: 'メンバー › 社会保険（日本） › 厚生年金保険の標準報酬月額'
  }, {
    label: '介護保険第2号被保険者区分',
    sourcePath: 'メンバー › 社会保険（日本） › 介護保険区分'
  }],
  rows: [['田村 拓真', 'EMP-1001', '東京都渋谷区神南 1-2-3', '東京都渋谷区', '東京都', '45', '5 等級', '主任', '2', '12,000', '5 号俸', '東京本社', '該当なし', '全国健康保険協会 東京支部', '一般被保険者', '加入', '定期', '2', '407,000', '適用', '該当（2026年7月改定）', '免除なし', '甲', '1981-05-14', '世帯主', 'みずほ銀行 渋谷支店 普通 1234501', '12340001-11', '1234-567801', '5012-345601-1', '410,000', '410,000', '該当'], ['岡部 美咲', 'EMP-1002', '東京都渋谷区代々木 4-5-6', '東京都渋谷区', '東京都', '32', '3 等級', '（なし）', '1', '8,000', '5 号俸', '東京本社', '該当なし', '全国健康保険協会 東京支部', '一般被保険者', '加入', '定期', '1', '249,000', '適用', '非該当', '免除なし', '甲', '1993-11-02', '世帯主以外', '三菱UFJ銀行 渋谷支店 普通 2234502', '12340001-12', '1234-567802', '5012-345602-2', '240,000', '240,000', '非該当'], ['川島 隆之', 'EMP-1003', '大阪府大阪市北区梅田 7-8-9', '大阪市', '大阪府', '51', '管理職 M2', '課長', '3', '15,000', '—', '大阪支社', '該当なし', '全国健康保険協会 大阪支部', '一般被保険者', '加入', '定期', '3', '480,000', '適用', '非該当', '免除なし', '甲', '1975-03-22', '世帯主', '三井住友銀行 梅田支店 普通 3234503', '12340001-13', '1234-567803', '5012-345603-3', '470,000', '470,000', '該当'], ['大久保 稔', 'EMP-1004', '大阪府大阪市浪速区難波 1-1-1', '大阪市', '大阪府', '67', '—', '（なし）', '0', '6,000', '—', '大阪支社', '一般障害者', '全国健康保険協会 大阪支部', '高年齢被保険者', '加入', '定期', '1', '118,000', '適用', '非該当', '免除なし', '甲', '1958-09-30', '世帯主以外', 'りそな銀行 難波支店 普通 4234504', '12340001-14', '1234-567804', '5012-345604-4', '118,000', '118,000', '非該当'], ['藤原 里美', 'EMP-1005', '神奈川県横浜市西区みなとみらい 2-2-2', '横浜市', '神奈川県', '54', '管理職 M1', '部長', '1', '18,000', '—', '横浜支社', '該当なし', '全国健康保険協会 神奈川支部', '一般被保険者', '加入', '定期', '1', '578,000', '適用', '非該当', '免除なし', '甲', '1972-01-18', '世帯主', '横浜銀行 本店営業部 普通 5234505', '12340001-15', '1234-567805', '5012-345605-5', '590,000', '590,000', '該当'], ['小柳 亮介', 'EMP-1006', '愛知県名古屋市中村区名駅 3-3-3', '名古屋市', '愛知県', '38', '5 等級', '（なし）', '0', '12,000', '1 号俸', '名古屋支社', '該当なし', '全国健康保険協会 愛知支部', '一般被保険者', '加入', '定期', '0', '344,000', '適用', '非該当', '免除なし', '甲', '1988-07-07', '世帯主', '三菱UFJ銀行 名古屋営業部 普通 6234506', '12340001-16', '1234-567806', '5012-345606-6', '340,000', '340,000', '非該当'], ['中原 千夏', 'EMP-1007', '福岡県福岡市博多区博多駅前 4-4-4', '福岡県福岡市', '福岡県', '40', '3 等級', '（なし）', '2', '9,000', '2 号俸', '福岡支社', '該当なし', '全国健康保険協会 福岡支部', '一般被保険者', '加入', '定期', '2', '238,000', '適用', '非該当', '免除なし', '甲', '1985-12-11', '世帯主以外', '福岡銀行 博多支店 普通 7234507', '12340001-17', '1234-567807', '5012-345607-7', '240,000', '240,000', '該当'], ['望月 康平', 'EMP-1008', '北海道札幌市中央区北一条西 5-5-5', '札幌市', '北海道', '47', '6 等級', '係長', '2', '11,000', '2 号俸', '札幌支社', '該当なし', '全国健康保険協会 北海道支部', '一般被保険者', '加入', '定期', '2', '371,500', '適用', '非該当', '免除なし', '甲', '1979-04-25', '世帯主', '北洋銀行 札幌駅前支店 普通 8234508', '12340001-18', '1234-567808', '5012-345608-8', '380,000', '380,000', '該当'], ['石垣 芽衣', 'EMP-1009', '埼玉県さいたま市大宮区桜木町 6-6-6', '埼玉県さいたま市', '埼玉県', '25', '—', '（なし）', '0', '7,000', '—', 'さいたま支社', '該当なし', '全国健康保険協会 埼玉支部', '一般被保険者', '加入', '定期', '0', '197,000', '適用', '非該当', '免除なし', '甲', '2001-06-03', '世帯主以外', '埼玉りそな銀行 大宮支店 普通 9234509', '12340001-19', '1234-567809', '5012-345609-9', '200,000', '200,000', '非該当'], ['篠原 正和', 'EMP-1010', '神奈川県川崎市川崎区駅前本町 7-7-7', '神奈川県川崎市', '神奈川県', '57', '4 等級', '主任', '1', '10,000', '3 号俸', '川崎支社', '該当なし', '全国健康保険協会 神奈川支部', '一般被保険者', '加入', '定期', '1', '329,000', '適用', '非該当', '免除なし', '甲', '1968-10-09', '世帯主', '横浜銀行 川崎支店 普通 1034510', '12340001-20', '1234-567810', '5012-345610-0', '320,000', '320,000', '該当']]
};